import React, { useEffect, useState } from "react";
import { Button, Form, Modal, ModalBody, ModalFooter, ModalHeader, Row, Col } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Spin } from "antd";
import { CarOutlined } from "@ant-design/icons";
import toast from "react-hot-toast";
import { createVehicule, updateVehicule } from "../services/vehiculeApi";
import { useQuery, useQueryClient } from "react-query";
import { getCatalogsByCategory } from "../../Catalogue/services/catalogueApi";
import { FilePond } from "react-filepond";
import { useImageUpload } from "../../../Components/Hooks/useImageUpload";
import "filepond/dist/filepond.min.css";
import { getUsers } from "../../Users/services/userApi";
import FormField from "../../../Components/Common/Form/FormField";
import SelectField from "../../../Components/Common/Form/SelectField";
import { deleteMedia, uploadImage } from "../../Media/services/mediaApi";
import FileField from "../../../Components/Common/Form/FileField";

const AddEditVehicleForm = ({ modal, setModal, toggle, selectedVehicule }) => {
  const queryClient = useQueryClient();
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const { image, handlePhotoFileUpdate } = useImageUpload();
  const statusType = "Véhicule"
  const { isLoading: isLoadingStatus, isError: isErrorStatus, error: errorStatus, data: statusVeh } = useQuery(
    ["status", statusType],
    () => getCatalogsByCategory(statusType)
  );

  const validationSchema = Yup.object().shape({
    brand: Yup.string().required("Veuillez renseigner la marque de la voiture"),
    model: Yup.string().required("Veuillez renseigner le modèle de la voiture"),
    description: Yup.string().optional(),
    transmission: Yup.string().optional(),
    speed: Yup.number().min(0, "La vitesse doit être positive").optional(),
    registrationNumber: Yup.string().required("Oups!!! Veuillez renseigner le numéro de la plaque"),
    commissioningDate: Yup.date().required("Veuillez renseigner la date de mise en service"),
    vin: Yup.string().required("Veuillez renseigner le VIN"),
    mileage: Yup.number().min(0, "Le kilométrage doit être positif").optional(),
    // fileName: Yup.string().optional(),
  });
  
  const initialValues= selectedVehicule ? {
      model: selectedVehicule.model,
      brand: selectedVehicule.brand,
      statut: selectedVehicule.statut,
      description: selectedVehicule.description,
      transmission: selectedVehicule.transmission,
      speed: selectedVehicule.speed,
      registrationNumber: selectedVehicule.registrationNumber,
      commissioningDate: selectedVehicule.commissioningDate,
      vin: selectedVehicule.vin,
      mileage: selectedVehicule.mileage,
      actualUserId: selectedVehicule.actualUserId,
      fileName: selectedVehicule.fileName      
    } : {
      brand: "",
      model: "",
      statut: "En service",
      description: "",
      transmission: "",
      speed: "",
      registrationNumber: "",
      commissioningDate: "",
      vin: "",
      mileage: "",
      actualUserId: "",
      fileName: "",
    }
  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values) => handleSubmit(values),
  });

  const handleSubmit = async (values) => {
    setIsLoadingSubmit(true);
    let fileName = values.fileName;
    
    try {
      if (image) {
        const response = await uploadImage("vehicle", image);
        fileName = response?.name;
      }
      if (selectedVehicule) {
        await updateVehicule(selectedVehicule.id, { ...values, fileName });
        toast.success("Voiture modifiée avec succès 🎉");
      } else {
        await createVehicule({ ...values, fileName });
        toast.success("Voiture ajoutée avec succès 🎉");
      }
      await queryClient.invalidateQueries("vehicules");
      toggle();
      formik.resetForm();
    } catch (error) {
      toast.error("Erreur : " + error?.message);
      if (image && fileName) {
        try {
          await deleteMedia(fileName);
        } catch (deleteError) {
          console.error("Erreur lors de la suppression de l'image :", deleteError);
        }
      }
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  useEffect(() => {
    formik.setValues(initialValues);
  }, [selectedVehicule]);

  const transmissionData = [{name:"Manuel"}, {name:"Automatique"}]

  return (
    <Modal isOpen={modal} toggle={toggle} centered backdrop={"static"} style={{ maxWidth: "800px" }}>
      <ModalHeader className="bg-info-subtle p-3" toggle={toggle}>
        {selectedVehicule ? "Édition" : "Ajout"} d'un véhicule <CarOutlined />
      </ModalHeader>
      <Form onSubmit={formik.handleSubmit}>
        <ModalBody>
          <Row>
            <FileField
                label="Photo du véhicule"
                name="fileName"
                formik={formik}
                file={image}
                onUpdateFiles={handlePhotoFileUpdate}
                helperText="La photo du véhicule peut etre optionnelle mais elle est recommandée"
                colSize={12}
              />
            <FormField label="Marque" name="brand" formik={formik} colSize={4} />
            <FormField label="Model" name="model" formik={formik} colSize={4} />
            <FormField label="Plaque" name="registrationNumber" formik={formik} colSize={4} />
            <FormField label="VIN" name="vin" formik={formik} colSize={4} />
            <FormField label="Date de mise en service" name="commissioningDate" type="date" formik={formik} colSize={4} />
            <FormField label="Kilométrage" name="mileage" type="number" formik={formik} colSize={4} />
            <FormField label="Vitesse" name="speed" type="number" formik={formik} colSize={4} />
            <SelectField label="Transmission" name="transmission" formik={formik} options={transmissionData} nameSelect = "le type de transmission" error={formik.errors.transmission} colSize={4} isName = {true} />
            <SelectField label="Statut" name="statut" formik={formik} isLoading={isLoadingStatus} isError={isErrorStatus} options={statusVeh} nameSelect = "le statut du véhicule" error={formik.errors.statut} colSize={4} isName = {true} />
            <FormField label="Description" name="description" formik={formik} colSize={12} isTextarea={true}/>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setModal(false)}>Annuler</Button>
          <Button type="submit" className="btn btn-soft-success" disabled={isLoadingSubmit}>
            {isLoadingSubmit ? <Spin size="small" /> : selectedVehicule ? "Modifier" : "Ajouter"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AddEditVehicleForm;
